import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Shield, Vote, BarChart3, Users, Lock, Globe,
  ChevronRight, Mail, ArrowRight, Eye,
  FileText, QrCode, Key, CheckCircle2, Fingerprint,
  Link2, Layers, MessageSquare, Send, Building2, User, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { LanguageSelector } from "../components/LanguageSelector";
import api from "../lib/api";
import { toast } from "sonner";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const contactRef = useRef(null);

  const [contactForm, setContactForm] = useState({
    name: "", email: "", entity_name: "", message: ""
  });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const response = await api.post("/entities/demo-login");
      localStorage.setItem("entity_id", response.data.entity_id);
      localStorage.setItem("entity_name", response.data.entity_name);
      localStorage.setItem("is_demo", "true");
      if (response.data.language) {
        localStorage.setItem("entity_language", response.data.language);
        i18n.changeLanguage(response.data.language);
      }
      navigate("/dashboard");
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setDemoLoading(false);
    }
  };

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error(t('contact.required'));
      return;
    }
    setContactSending(true);
    try {
      await api.post("/contact/", contactForm);
      setContactSent(true);
      toast.success(t('contact.success'));
      setContactForm({ name: "", email: "", entity_name: "", message: "" });
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setContactSending(false);
    }
  };

  const features = [
    { icon: Vote, titleKey: "landing.feat1Title", descKey: "landing.feat1Desc", color: "bg-blue-500" },
    { icon: Shield, titleKey: "landing.feat2Title", descKey: "landing.feat2Desc", color: "bg-emerald-500" },
    { icon: BarChart3, titleKey: "landing.feat3Title", descKey: "landing.feat3Desc", color: "bg-amber-500" },
    { icon: Users, titleKey: "landing.feat4Title", descKey: "landing.feat4Desc", color: "bg-violet-500" },
    { icon: FileText, titleKey: "landing.feat5Title", descKey: "landing.feat5Desc", color: "bg-rose-500" },
    { icon: Globe, titleKey: "landing.feat6Title", descKey: "landing.feat6Desc", color: "bg-teal-500" },
  ];

  const security = [
    { icon: Lock, labelKey: "landing.sec1" },
    { icon: Key, labelKey: "landing.sec2" },
    { icon: Fingerprint, labelKey: "landing.sec3" },
    { icon: Link2, labelKey: "landing.sec4" },
    { icon: Layers, labelKey: "landing.sec5" },
    { icon: CheckCircle2, labelKey: "landing.sec6" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img
              src="/api/uploads/blockchain_tools_logo.png"
              alt="Blockchain Tools - Plataforma de votació digital segura"
              className="h-10 w-auto"
              width="160"
              height="40"
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={scrollToContact}
              className="hidden sm:inline-flex text-slate-600 hover:text-slate-900"
              data-testid="nav-contact-btn"
            >
              {t('contact.navButton')}
            </Button>
            <LanguageSelector />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/login")}
              data-testid="landing-login-btn"
            >
              {t('auth.login')}
            </Button>
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleDemoLogin}
              disabled={demoLoading}
              data-testid="landing-demo-btn"
            >
              <Eye className="h-4 w-4 mr-1" />
              {t('demo.demoButton')}
            </Button>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" aria-labelledby="hero-heading">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: "radial-gradient(circle at 25% 25%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(16,185,129,0.3) 0%, transparent 50%)"
            }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36 relative">
            <motion.div
              initial="hidden" animate="visible" variants={stagger}
              className="max-w-3xl"
            >
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm mb-8 backdrop-blur-sm border border-white/10">
                <Shield className="h-4 w-4 text-emerald-400" />
                <span className="text-slate-300">{t('landing.badge')}</span>
              </motion.div>
              <motion.h1 id="hero-heading" variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                {t('landing.heroTitle')}
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
                {t('landing.heroSubtitle')}
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-white h-12 px-8 text-base"
                  onClick={handleDemoLogin}
                  disabled={demoLoading}
                  data-testid="hero-demo-btn"
                >
                  <Eye className="h-5 w-5 mr-2" />
                  {t('landing.heroDemo')}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 h-12 px-8 text-base"
                  onClick={scrollToContact}
                  data-testid="hero-contact-btn"
                >
                  <MessageSquare className="h-5 w-5 mr-2" />
                  {t('contact.navButton')}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 sm:py-28 bg-slate-50" id="features" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="text-center mb-16"
            >
              <motion.h2 id="features-heading" variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-slate-900">
                {t('landing.featTitle')}
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                {t('landing.featSubtitle')}
              </motion.p>
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {features.map((feat, i) => (
                <motion.article
                  key={i} variants={fadeUp}
                  className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className={`${feat.color} w-12 h-12 rounded-xl flex items-center justify-center mb-5`}>
                    <feat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{t(feat.titleKey)}</h3>
                  <p className="text-slate-600 leading-relaxed">{t(feat.descKey)}</p>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Security */}
        <section className="py-20 sm:py-28 bg-white" aria-labelledby="security-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="text-center mb-16"
            >
              <motion.h2 id="security-heading" variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-slate-900">
                {t('landing.secTitle')}
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                {t('landing.secSubtitle')}
              </motion.p>
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="grid grid-cols-2 md:grid-cols-3 gap-6"
            >
              {security.map((sec, i) => (
                <motion.div
                  key={i} variants={fadeUp}
                  className="flex items-center gap-4 bg-slate-50 rounded-xl p-5 border border-slate-100"
                >
                  <div className="bg-slate-900 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <sec.icon className="h-5 w-5 text-emerald-400" aria-hidden="true" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{t(sec.labelKey)}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 sm:py-28 bg-slate-50" aria-labelledby="how-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="text-center mb-16"
            >
              <motion.h2 id="how-heading" variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-slate-900">
                {t('landing.howTitle')}
              </motion.h2>
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="grid grid-cols-1 md:grid-cols-4 gap-8"
            >
              {[1, 2, 3, 4].map((step) => (
                <motion.div key={step} variants={fadeUp} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4" aria-hidden="true">
                    {step}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{t(`landing.step${step}Title`)}</h3>
                  <p className="text-sm text-slate-600">{t(`landing.step${step}Desc`)}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Contact Form */}
        <section
          ref={contactRef}
          id="contact"
          className="py-20 sm:py-28 bg-white"
          aria-labelledby="contact-heading"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            >
              <motion.div variants={fadeUp} className="text-center mb-12">
                <h2 id="contact-heading" className="text-3xl sm:text-4xl font-bold text-slate-900">
                  {t('contact.title')}
                </h2>
                <p className="mt-4 text-base sm:text-lg text-slate-600">
                  {t('contact.subtitle')}
                </p>
              </motion.div>

              {contactSent ? (
                <motion.div
                  variants={fadeUp}
                  className="text-center py-16 bg-emerald-50 rounded-2xl border border-emerald-100"
                  data-testid="contact-success-message"
                >
                  <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {t('contact.thankYouTitle')}
                  </h3>
                  <p className="text-slate-600">{t('contact.thankYouDesc')}</p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => setContactSent(false)}
                    data-testid="contact-send-another-btn"
                  >
                    {t('contact.sendAnother')}
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  variants={fadeUp}
                  onSubmit={handleContactSubmit}
                  className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-100 space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="flex items-center gap-2 text-slate-700">
                        <User className="h-4 w-4" />
                        {t('contact.name')} *
                      </Label>
                      <Input
                        id="contact-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('contact.namePlaceholder')}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="flex items-center gap-2 text-slate-700">
                        <Mail className="h-4 w-4" />
                        {t('contact.email')} *
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder={t('contact.emailPlaceholder')}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-entity" className="flex items-center gap-2 text-slate-700">
                      <Building2 className="h-4 w-4" />
                      {t('contact.entityName')}
                    </Label>
                    <Input
                      id="contact-entity"
                      value={contactForm.entity_name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, entity_name: e.target.value }))}
                      placeholder={t('contact.entityPlaceholder')}
                      data-testid="contact-entity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="flex items-center gap-2 text-slate-700">
                      <MessageSquare className="h-4 w-4" />
                      {t('contact.message')} *
                    </Label>
                    <Textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder={t('contact.messagePlaceholder')}
                      rows={5}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12"
                    disabled={contactSending}
                    data-testid="contact-submit-btn"
                  >
                    {contactSending ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Send className="h-5 w-5 mr-2" />
                    )}
                    {contactSending ? t('contact.sending') : t('contact.send')}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    {t('contact.privacy')}
                  </p>
                </motion.form>
              )}
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 sm:py-28 bg-gradient-to-br from-slate-900 to-slate-800 text-white" aria-labelledby="cta-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.h2 id="cta-heading" variants={fadeUp} className="text-3xl sm:text-4xl font-bold">
                {t('landing.ctaTitle')}
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-4 text-base sm:text-lg text-slate-300">
                {t('landing.ctaSubtitle')}
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-white h-12 px-8 text-base"
                  onClick={handleDemoLogin}
                  disabled={demoLoading}
                >
                  <Eye className="h-5 w-5 mr-2" />
                  {t('landing.heroDemo')}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 h-12 px-8 text-base"
                  onClick={() => navigate("/register")}
                >
                  {t('landing.ctaRegister')}
                  <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/api/uploads/blockchain_tools_logo_white.png" alt="Blockchain Tools" className="h-8 w-auto" width="128" height="32" />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <a href="mailto:info@blockchaintools.es" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="h-4 w-4" aria-hidden="true" />
                info@blockchaintools.es
              </a>
            </div>
            <p className="text-xs text-slate-500">
              &copy; {new Date().getFullYear()} Blockchain Tools SCCL
            </p>
          </div>
        </div>
      </footer>

      {/* Floating contact button */}
      <button
        onClick={scrollToContact}
        className="fixed bottom-6 right-6 z-50 bg-slate-900 hover:bg-slate-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 group"
        aria-label={t('contact.navButton')}
        data-testid="floating-contact-btn"
      >
        <MessageSquare className="h-6 w-6 group-hover:scale-110 transition-transform" />
      </button>
    </div>
  );
}
